<template>
  <section>
    <div>
      <SeoHero :scene="scene" bike-type="mountain" @cta="handleClick">
        Mountain Bike Insurance <br />You Can Trust
      </SeoHero>

      <div class="container">
        <Featured />
        <Thrillingly />
        <PeaceOfMind />
        <BlogWidget
          title="Learn more about mountain biking"
          :slugs="[
            'road-cycling-vs-mountain-biking-which-is-best-for-you',
            'how-to-get-started-in-mountain-biking',
            '10-best-gravel-roads-in-europe',
            '5-best-mountain-biking-destinations-in-the-uk',
          ]"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions } from 'vuex'
import Featured from '@/components/homepage/Featured'
import Thrillingly from '@/components/commuter/Thrillingly'
import PeaceOfMind from '@/components/ebike/PeaceOfMind'
import SeoHero from '@/components/seo/SeoHero'
import BlogWidget from '@/components/blog-widget/BlogWidget'
import { buildSocialMeta, resolveSocialImage } from '@/helpers/social-meta'
import { productOffer } from '@/helpers/product-offer'

// og:image and Product.image both read this, so the two cannot drift apart.
const OG_IMAGE_PATH = '/images/og/mountain-bike-insurance-1200x630.png'

// The bike this page is about: drives the CTA href and the modal prefill.
const BIKE_TYPE = 'mountain'

// Hero animation: two enduro riders jumping along a woodland trail. Config
// for helpers/pedalsure-scene.js, as supplied by design; the palette maps the
// engine's defaults onto the brand tokens in assets/_variables.scss.
const HERO_SCENE = {
  height: 420,
  groundY: 340,
  speed: 210,
  palette: { blue: '#3e4cd4', yellow: '#f5c13b', green: '#17803d' },
  terrain: {
    type: 'trail',
    waves: [
      [1, 16, 0],
      [2, 10, 1.3],
      [4, 5, 0.4],
    ],
  },
  layers: [
    { kind: 'sun', x: 1010, y: 70, r: 36 },
    {
      kind: 'clouds',
      depth: 0.04,
      clouds: [
        [80, 0.05, 0.9],
        [560, 0.14, 0.8],
        [860, 0.02, 1],
      ],
    },
    {
      kind: 'hills',
      depth: 0.08,
      base: 250,
      height: 110,
      color: 'lav',
      waves: [
        [1, 0],
        [3, 2],
      ],
    },
    {
      kind: 'pines',
      depth: 0.16,
      base: 270,
      color: 'lav',
      lineColor: 'lavMid',
      seed: 4,
      heights: [50, 90],
    },
    {
      kind: 'hills',
      depth: 0.25,
      base: 320,
      height: 70,
      color: 'lavDeep',
      waves: [
        [2, 1],
        [3, 0],
      ],
    },
    {
      kind: 'pines',
      depth: 0.42,
      base: 356,
      color: 'spoke',
      lineColor: 'blue',
      seed: 9,
      spacing: 120,
    },
    { kind: 'trail', depth: 1 },
    {
      kind: 'signs',
      depth: 1,
      at: [980],
      lines: ['Woodland trail ▸', '◂ Black run', '◂ Blue run'],
    },
  ],
  riders: [
    {
      bike: 'enduro',
      x: 360,
      scale: 0.72,
      jersey: 'yellow',
      accent: 'yellow',
      helmet: 'blue',
      frame: 'blue',
      phase: 2,
    },
    {
      bike: 'enduro',
      x: 640,
      scale: 0.82,
      jersey: 'blue',
      accent: 'blue',
      helmet: 'yellow',
      frame: 'pearl',
    },
  ],
  story: { type: 'jump', every: 4.8, height: 50, stagger: 1.3 },
}

export default {
  name: 'MountainBikeInsurance',
  components: {
    Featured,
    Thrillingly,
    PeaceOfMind,
    BlogWidget,
    SeoHero,
  },
  data() {
    return {
      // Frozen so Vue skips making the config reactive; the engine reads it once.
      scene: Object.freeze(HERO_SCENE),
      page: {
        title: 'Mountain Bike Insurance (You Can Trust) | Pedalsure',
        meta_description:
          'Quality mountain bike insurance from Pedalsure. Protect your mountain bike against theft and accidental damage and cover yourself against personal injury.',
      },
    }
  },
  head() {
    return {
      title: this.page.title,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.page.meta_description,
        },
        { hid: 'title', name: 'title', content: this.page.title },
        {
          hid: 'keywords',
          name: 'keywords',
          content: 'commuter insurance, road, cycling',
        },
        ...buildSocialMeta({
          title: this.page.title,
          description: this.page.meta_description,
          path: this.$route.path,
          image: OG_IMAGE_PATH,
          type: 'website',
        }).meta,
      ],
      script: [
        {
          innerHTML: `
        {
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "Mountain Bike Insurance",
          "logo": "https://www.pedalsure.com/Pedalsure-Logo_Large.svg",
          "image": ${JSON.stringify([resolveSocialImage(OG_IMAGE_PATH)])},
          "description": "Quality mountain bike insurance from Pedalsure. Protect your mountain bike against theft and accidental damage as standard, with optional personal accident and liability cover.",
           "brand": {
            "@type": "Brand",
            "name": "Pedalsure"
          },
          "offers": ${JSON.stringify(productOffer())}
        }
        `,
          type: 'application/ld+json',
        },
      ],
    }
  },
  methods: {
    ...mapActions('quote', {
      resetQuote: 'resetQuote',
    }),
    handleClick() {
      this.resetQuote()
      // Same values the CTA's href carries, so a quote started from the modal
      // is attributed identically to one started by following the link.
      this.$modal.show('quick-quote', {
        bikeType: BIKE_TYPE,
        src: `lp-${BIKE_TYPE}`,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.container {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 20px;
}
</style>

<style lang="scss" scoped>
/* Hero styling now lives in components/seo/SeoHero.vue */
</style>
